import { useEffect } from "react";
import events from "../../../utils/constants/events";
import { Eventbus } from "../../../utils";
import { mergeSignalRMessage } from "../../../utils/helpers";
import { serializeDraftForComparison } from "../helpers";

export default function useSignalRSync({
  draft,
  historySelection,
  setDraft,
  setSavedDraftSnapshot,
  setHistorySelection,
  setScorecardHistory,
  setSubordinates,
  selectedStaffEmail,
  service,
}) {
  useEffect(() => {
    const applyToDraft = (updater) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const next = updater(prev);
        if (next === prev) return prev;
        queueMicrotask(() => setSavedDraftSnapshot(serializeDraftForComparison(next)));
        return next;
      });
      setHistorySelection((prev) => {
        if (!prev.scorecard) return prev;
        const next = updater(prev.scorecard);
        if (next === prev.scorecard) return prev;
        return { ...prev, scorecard: next };
      });
    };

    const getActiveKpiDataId = () => draft?.kpiDataId ?? historySelection.scorecard?.kpiDataId ?? null;

    const handleScorecardUpdated = (message) => {
      if (!message?.kpiDataId) return;
      const activeId = getActiveKpiDataId();
      if (message.kpiDataId !== activeId) return;

      applyToDraft((current) => {
        if (!Array.isArray(message.groups)) return current;

        const incomingByName = new Map(
          message.groups.map((g) => [g.groupName, g])
        );

        // Update existing groups with incoming data, preserve order
        const merged = current.groups.map((existing) => {
          const incoming = incomingByName.get(existing.groupName);
          if (!incoming) return existing; // preserve groups missing from message
          incomingByName.delete(existing.groupName);
          return incoming;
        });

        // Append any genuinely new groups from the message
        for (const newGroup of incomingByName.values()) {
          merged.push(newGroup);
        }

        return { ...current, groups: merged };
      });
    };

    const handleFeedbackCreated = (message) => {
      if (!message?.kpiDataId) return;
      const activeId = getActiveKpiDataId();
      if (message.kpiDataId !== activeId) return;

      applyToDraft((current) => {
        const next = mergeSignalRMessage(current.feedbacks, message);
        return next === current.feedbacks ? current : { ...current, feedbacks: next };
      });
    };

    const handleFeedbackDeleted = (message) => {
      const feedbackId = message?.feedbackId ?? message;
      if (feedbackId == null) return;
      const activeId = getActiveKpiDataId();
      if (message?.kpiDataId && message.kpiDataId !== activeId) return;

      applyToDraft((current) => {
        const next = mergeSignalRMessage(current.feedbacks, null, { deleteId: feedbackId });
        return next === current.feedbacks ? current : { ...current, feedbacks: next };
      });
    };

    const handleGoalUpdated = (message) => {
      if (!message?.kpiDataId) return;

      // Always update history goalCount — even when this scorecard isn't active
      setScorecardHistory((h) => {
        const match = h.find((item) => item.kpiDataId === message.kpiDataId);
        if (!match) return h;
        // Only increment if this goal id doesn't already exist in cached goals
        const cachedGoals = service.getCachedGoals(message.kpiDataId);
        const alreadyExists = cachedGoals?.some((g) => g.id === message.id);
        if (alreadyExists) return h;
        return h.map((item) =>
          item.kpiDataId === message.kpiDataId
            ? { ...item, goalCount: (item.goalCount ?? 0) + 1 }
            : item
        );
      });

      // Mirror goalCount on the team dropdown entry for the selected staff member
      if (selectedStaffEmail) {
        const cachedGoals = service.getCachedGoals(message.kpiDataId);
        const alreadyExists = cachedGoals?.some((g) => g.id === message.id);
        if (!alreadyExists) {
          setSubordinates?.((subs) =>
            subs.map((s) =>
              s.employeeEmail === selectedStaffEmail
                ? { ...s, goalCount: (s.goalCount ?? 0) + 1 }
                : s
            )
          );
        }
      }

      // Only update the draft if this scorecard is currently active
      const activeId = getActiveKpiDataId();
      if (message.kpiDataId !== activeId) return;

      applyToDraft((current) => {
        const next = mergeSignalRMessage(current.goals, message, { timestampField: 'modifiedAt' });
        if (next === current.goals) return current;
        return { ...current, goals: next };
      });
    };

    const handleGoalDeleted = (message) => {
      const goalId = message?.goalId ?? message;
      if (goalId == null) return;

      // Always update history goalCount — even when this scorecard isn't active
      const kpiDataId = message?.kpiDataId;
      if (kpiDataId) {
        setScorecardHistory((h) =>
          h.map((item) =>
            item.kpiDataId === kpiDataId
              ? { ...item, goalCount: Math.max((item.goalCount ?? 1) - 1, 0) }
              : item
          )
        );
        if (selectedStaffEmail) {
          setSubordinates?.((subs) =>
            subs.map((s) =>
              s.employeeEmail === selectedStaffEmail
                ? { ...s, goalCount: Math.max((s.goalCount ?? 1) - 1, 0) }
                : s
            )
          );
        }
      }

      // Only update the draft if this scorecard is currently active
      const activeId = getActiveKpiDataId();
      if (message?.kpiDataId && message.kpiDataId !== activeId) return;

      applyToDraft((current) => {
        const next = mergeSignalRMessage(current.goals, null, { deleteId: goalId });
        if (next === current.goals) return current;
        return { ...current, goals: next };
      });
    };

    Eventbus.on(events.SIGNALR.SCORECARD_UPDATED, handleScorecardUpdated);
    Eventbus.on(events.SIGNALR.SCORECARD_FEEDBACK_CREATED, handleFeedbackCreated);
    Eventbus.on(events.SIGNALR.SCORECARD_FEEDBACK_DELETED, handleFeedbackDeleted);
    Eventbus.on(events.SIGNALR.SCORECARD_GOAL_UPDATED, handleGoalUpdated);
    Eventbus.on(events.SIGNALR.SCORECARD_GOAL_DELETED, handleGoalDeleted);

    return () => {
      Eventbus.off(handleScorecardUpdated);
      Eventbus.off(handleFeedbackCreated);
      Eventbus.off(handleFeedbackDeleted);
      Eventbus.off(handleGoalUpdated);
      Eventbus.off(handleGoalDeleted);
    };
  }, [draft?.kpiDataId, historySelection.scorecard?.kpiDataId, selectedStaffEmail, setSubordinates]);
}
